import Noty from 'noty';

import { NOTIFY_TIMEOUT } from '@/constants';

const notificationIcons = {
  success: 'circle-check',
  error: 'circle-close',
  warning: 'circle-warning',
  info: 'circle-info',
} as const;

export type NotificationType = keyof typeof notificationIcons;

interface NotyOptions extends Noty.Options {
  type?: NotificationType;
}

export interface Notify {
  options: NotyOptions;
  /**
   * Internal private property of `noty`'s implementation that can be accessed inside callback methods
   */
  barDom?: HTMLDivElement;
  show: (text: string, type: NotificationType, opts?: NotyOptions) => void;
  success: (text: string, opts?: NotyOptions) => void;
  error: (text: string, opts?: NotyOptions) => void;
  warning: (text: string, opts?: NotyOptions) => void;
  info: (text: string, opts?: NotyOptions) => void;
}

const notify: Notify = {
  options: {
    layout: 'topRight',
    timeout: NOTIFY_TIMEOUT,
    progressBar: true,
    closeWith: ['click'],
    callbacks: {
      onTemplate: function () {
        const { barDom, options } = this as Notify;
        // Enforcing `this.barDom` to be the element since it's tight to
        // internal Noty implementation, not covered on their provided types
        (barDom as HTMLDivElement).innerHTML = `
          <div class="noty_body align-middle">
            <svg class="noty_icon"><use xlink:href="/static/spritesheet.svg#${
              notificationIcons[options.type as string]
            }" /></svg>
            <div class="text_body" data-test="popup-notify-message">${options.text}</div>
          </div>
        `;
      },
    },
  },

  show(text, type = 'success', opts = {}) {
    return new Noty({ ...this.options, ...opts, type, text }).show();
  },

  success(text, opts = {}) {
    return this.show(text, 'success', opts);
  },

  error(text, opts = {}) {
    return this.show(text, 'error', opts);
  },

  warning(text, opts = {}) {
    return this.show(text, 'warning', opts);
  },

  info(text, opts = {}) {
    return this.show(text, 'info', opts);
  },
};

export default notify;
